import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Card, Button, Tag, Typography, Row, Col,
  Space, Popconfirm, message, Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  StopOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import BotonBloqueado from '../../components/BotonBloqueado';
import { useAuth } from '../../context/AuthContext';
import { eventoService } from '../../services/api';

const { Title } = Typography;

const EventosList = () => {
  const { user, tieneRecurso } = useAuth();
  /* ¿Este remate lo organizo yo, o me invitaron a consignar?
   *
   * Desde que `filtro_eventos_visibles` incluye los remates ajenos donde
   * consigno, la lista mezcla las dos cosas y la pantalla tiene que saber
   * distinguirlas. El backend ya lo sabe —`ensure_owner` sigue estricto en todo
   * lo que escribe— pero si la UI no lo sabe, ofrece botones que terminan en
   * 403, que es exactamente lo que este panel vino a eliminar. */
  const esPropio = (evento) => Boolean(evento && user && evento.id_clientes === user.id_clientes);

  /* Por que NO se puede escribir sobre este remate, o null si si se puede.
   *
   * Son DOS ejes distintos y el orden importa: primero la propiedad, porque
   * ningun plan arregla que el remate sea de otro. Ofrecerle un upgrade a quien
   * mira el remate del vecino seria mentirle.
   *
   * El eje del PLAN cubre un caso que es facil de pasar por alto: una cabania
   * con la SUSCRIPCION VENCIDA conserva `ver_eventos` y pierde `crud_eventos`
   * (`recursos_efectivos` saca todo lo que es escritura). Ve sus propios
   * remates, `esPropio` da true, y sin este chequeo tendria Editar vivo: llena
   * el formulario y pierde el trabajo en el 403. Es el mismo final destructivo
   * que el del remate ajeno, por la otra puerta. */
  const motivoBloqueo = (evento) => {
    if (!esPropio(evento)) return 'ajeno';
    if (!tieneRecurso('crud_eventos')) return 'plan';
    return null;
  };

  const [loading, setLoading] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchEventos();
  }, [pagination.current]);

  const fetchEventos = async () => {
    setLoading(true);
    try {
      // compacto=1: solo los campos que muestra la grilla. Evita serializar
      // imgsec_evento (URL larga), mensaje_oferta_1/2 (HTML), configuracion_micrositio
      // y categoria_evento (JSON), que la grilla no usa.
      const response = await eventoService.getAll({
        page: pagination.current,
        per_page: pagination.pageSize,
        compacto: 1,
      });
      setEventos(response.data.eventos);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await eventoService.delete(id);
      message.success('Evento eliminado');
      fetchEventos();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleFinalizar = async (id) => {
    try {
      await eventoService.finalizar(id);
      message.success('Evento finalizado');
      fetchEventos();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al finalizar');
    }
  };

  const handleReactivar = async (id) => {
    try {
      await eventoService.reactivar(id);
      message.success('Evento reactivado');
      fetchEventos();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al reactivar');
    }
  };

  const handleTableChange = (pag) => {
    setPagination(prev => ({
      ...prev,
      current: pag.current,
    }));
  };

  const columns = [
    {
      title: 'Evento',
      dataIndex: 'nombre_evento',
      key: 'nombre_evento',
      render: (text, record) => (
        <a onClick={() => navigate(`/eventos/${record.id_evento}`)}>{text}</a>
      ),
    },
    {
      title: 'Fecha Inicio',
      dataIndex: 'fecha_inicio',
      key: 'fecha_inicio',
    },
    {
      title: 'Fecha Fin',
      dataIndex: 'fecha_fin',
      key: 'fecha_fin',
    },
    {
      title: 'Horario',
      key: 'horario',
      render: (_, record) => (
        record.hora_inicio ? `${record.hora_inicio} - ${record.hora_fin}` : '-'
      ),
    },
    {
      // Antes era "Cliente" y para el titular decia SIEMPRE su propia razon
      // social: un dato que no informa nada. Desde que la lista incluye los
      // remates ajenos donde consigno, lo que hace falta saber es de quien es.
      title: 'Tipo',
      key: 'tipo',
      render: (_, record) => (
        esPropio(record)
          ? <Tag color="blue">Propio</Tag>
          : (
            <Tooltip title={record.cliente?.razon_social || 'Otra cabaña'}>
              <Tag>Invitado</Tag>
            </Tooltip>
          )
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, record) => {
        if (!record.es_finalizado) {
          return <Tag color="green">Activo</Tag>;
        }
        if (record.motivo_finalizacion === 'vencido') {
          return <Tag color="volcano">Vencido</Tag>;
        }
        return <Tag color="red">Finalizado</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/eventos/${record.id_evento}`)}
            />
          </Tooltip>
          {!motivoBloqueo(record) ? (
            <Tooltip title="Editar">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/eventos/${record.id_evento}/editar`)}
              />
            </Tooltip>
          ) : (
            // Bloqueado y NO oculto: el invitado tiene que entender por que no
            // puede, y "es de otra cabaña" es una explicacion que se da una vez
            // y se entiende para siempre.
            //
            // Y no es cosmetico. Antes el boton navegaba, el formulario cargaba
            // COMPLETO —porque la lectura del evento si esta abierta para el
            // invitado— y recien al apretar Guardar el PUT devolvia 403. O sea
            // que el productor llenaba el formulario del remate de otro y perdia
            // el trabajo. Un boton muerto es molesto; ese era destructivo.
            <BotonBloqueado motivo={motivoBloqueo(record)} type="text" icon={<EditOutlined />} />
          )}
          {motivoBloqueo(record) ? null : !record.es_finalizado ? (
            <Popconfirm
              title="¿Finalizar este evento?"
              description="Se cerrará el micrositio y las ofertas."
              onConfirm={() => handleFinalizar(record.id_evento)}
              okText="Finalizar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Finalizar evento">
                <Button type="text" icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : (
            record.motivo_finalizacion === 'manual' && (
              <Popconfirm
                title="¿Reactivar este evento?"
                onConfirm={() => handleReactivar(record.id_evento)}
                okText="Reactivar"
                cancelText="Cancelar"
              >
                <Tooltip title="Reactivar evento">
                  <Button type="text" icon={<ReloadOutlined />} />
                </Tooltip>
              </Popconfirm>
            )
          )}
          {!motivoBloqueo(record) ? (
            <Popconfirm
              title="¿Eliminar este evento?"
              onConfirm={() => handleDelete(record.id_evento)}
              okText="Sí"
              cancelText="No"
            >
              <Tooltip title="Eliminar">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : (
            <BotonBloqueado motivo={motivoBloqueo(record)} type="text" danger icon={<DeleteOutlined />} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Eventos</Title>
        </Col>
        <Col>
          {/* Crear tampoco es gratis: `POST /eventos/` pide `crud_eventos`. Sin
              esto, la cabania con la suscripcion vencida arma un remate entero
              y lo pierde al guardar. Aca el motivo es SIEMPRE el plan — no hay
              eje de propiedad, el remate todavia no existe. */}
          {tieneRecurso('crud_eventos') ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/eventos/nuevo')}
            >
              Nuevo Evento
            </Button>
          ) : (
            <BotonBloqueado motivo="plan" type="primary" icon={<PlusOutlined />}>
              Nuevo Evento
            </BotonBloqueado>
          )}
        </Col>
      </Row>

      <Card>
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={eventos}
          rowKey="id_evento"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} eventos`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default EventosList;
